import type { StaffUser } from "./api";
import { authApi, ApiRequestError } from "./api";
import {
  clearSession,
  getStaffUser,
  setStoredStaff,
} from "./auth-storage";

let bootstrapComplete = false;
let bootstrapInFlight: Promise<StaffUser | null> | null = null;

/** True once the initial auth bootstrap has finished (success or failure). */
export function isAuthBootstrapComplete(): boolean {
  return bootstrapComplete;
}

export function resetAuthBootstrapState(): void {
  bootstrapComplete = false;
  bootstrapInFlight = null;
}

/**
 * Session presence comes from GET /auth/staff/me with the host cookie.
 * HttpOnly cookies are invisible to script, so this always asks the API.
 */
export function bootstrapAuthenticatedStaff(): Promise<StaffUser | null> {
  if (bootstrapInFlight) return bootstrapInFlight;

  bootstrapComplete = false;
  const pending = authApi
    .getStaffMe()
    .then((staff) => {
      setStoredStaff(staff);
      return staff;
    })
    .catch((err: unknown) => {
      if (err instanceof ApiRequestError && err.status === 429) {
        const cached = getStaffUser();
        if (cached) return cached;
      }
      clearSession();
      return null;
    })
    .finally(() => {
      bootstrapComplete = true;
      bootstrapInFlight = null;
    });

  bootstrapInFlight = pending;
  return pending;
}

export function markAuthBootstrapSettled(): void {
  bootstrapComplete = true;
  bootstrapInFlight = null;
}

/** Wait until the one-time bootstrap finishes (no-op if already settled). */
export function waitForAuthBootstrap(): Promise<void> {
  if (bootstrapComplete) return Promise.resolve();
  if (bootstrapInFlight) return bootstrapInFlight.then(() => undefined);
  return Promise.resolve();
}
